import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Building2, Users, CalendarCheck, Receipt, TrendingUp,
  Wallet, ShieldCheck, Settings, LogOut, Menu, X, ChevronDown, HandCoins, BedDouble, Landmark, Coins, PiggyBank,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import syakuraLogo from "@/assets/syakura-logo.png";

function setFavicon(url) {
  let link = document.head.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.type = "image/png";
  link.href = url;
}

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/proyek-kamar", label: "Proyek & Kamar", icon: Building2 },
  { to: "/penghuni", label: "Penghuni", icon: Users },
  { to: "/booking-sewa", label: "Booking & Sewa", icon: CalendarCheck },
  { to: "/pembayaran", label: "Pembayaran", icon: Receipt },
  { to: "/riwayat-deposit", label: "Riwayat Deposit", icon: PiggyBank },
  { to: "/guest-house", label: "Guest House", icon: BedDouble },
  { to: "/investor-laporan", label: "Investor & Laporan", icon: TrendingUp },
  { to: "/angsuran-investor", label: "Angsuran Investor", icon: Landmark },
  { to: "/pencairan-investor", label: "Pencairan Investor", icon: HandCoins },
  { to: "/rekap-keuangan", label: "Rekap Keuangan", icon: Wallet },
  { to: "/keuangan-operasional", label: "Keuangan Operasional", icon: Coins, superadmin: true },
  { to: "/persetujuan", label: "Persetujuan", icon: ShieldCheck, superadmin: true },
  { to: "/pengaturan", label: "Pengaturan", icon: Settings },
];

function Brand({ settings, logoUrl }) {
  return (
    <div className="flex items-center gap-3 px-2">
      <img
        src={logoUrl || syakuraLogo}
        alt="Logo"
        className="h-10 w-10 rounded-lg object-contain bg-white/10 p-1"
      />
      <div className="leading-tight">
        <div className="text-sm font-bold text-white">{settings?.manager_name || "Syakura"}</div>
        <div className="text-[11px]" style={{ color: "hsl(var(--sidebar-muted))" }}>
          Manajemen Kos
        </div>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout, isSuperadmin } = useAuth();
  const { projects, projectId, setProjectId } = useProjects();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });

  useEffect(() => {
    let revoked = null;
    if (settings?.has_logo) {
      api.get("/settings/logo", { responseType: "blob" }).then((res) => {
        const url = URL.createObjectURL(res.data);
        revoked = url;
        setLogoUrl(url);
        setFavicon(url);
      }).catch(() => {});
    }
    return () => revoked && URL.revokeObjectURL(revoked);
  }, [settings?.has_logo, settings?.logo_mime]);

  const navItems = NAV.filter((n) => !n.superadmin || isSuperadmin);

  const SidebarContent = () => (
    <div className="flex h-full flex-col" style={{ background: "hsl(var(--sidebar))" }}>
      <div className="flex items-center justify-between py-5 pr-3">
        <Brand settings={settings} logoUrl={logoUrl} />
        <button className="lg:hidden text-white/70 pr-1" onClick={() => setMobileOpen(false)} data-testid="sidebar-close">
          <X className="h-5 w-5" />
        </button>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4">
        {navItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setMobileOpen(false)}
            data-testid={`sidebar-nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
            className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link-active" : ""}`}
          >
            <n.icon className="h-[18px] w-[18px] shrink-0" />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t p-3" style={{ borderColor: "hsl(var(--sidebar-active))" }}>
        <div className="mb-2 px-2 text-xs" style={{ color: "hsl(var(--sidebar-muted))" }}>
          Masuk sebagai
        </div>
        <div className="flex items-center justify-between gap-2 px-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-white">{user?.name}</div>
            <div className="truncate text-[11px]" style={{ color: "hsl(var(--sidebar-muted))" }}>
              {user?.role === "superadmin" ? "Superadmin" : "Admin Proyek"}
            </div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-button"
            className="rounded-lg p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            title="Keluar"
          >
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64">
            <SidebarContent />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-white px-4 md:px-6">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="sidebar-open">
            <Menu className="h-5 w-5 text-foreground" />
          </button>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs font-medium text-muted-foreground sm:block">Proyek</span>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="h-9 w-[180px] md:w-[220px]" data-testid="filter-proyek-select">
                <SelectValue placeholder="Semua Proyek" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Proyek</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`project-option-${p.code}`}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold leading-tight text-foreground">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground">{user?.email}</div>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
